import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useAuthStore } from '@/stores/authStore'
import { authService } from '@/services/api'
import {
  loginFormSchema,
  defaultLoginValues,
  otpFormSchema,
  defaultOtpValues,
} from './LoginForm.static'
import type { LoginFormValues, OtpFormValues, LoginStep } from './LoginForm.static'
import type { ApiError } from '@/types'

export function useLoginForm() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const [showPassword, setShowPassword] = useState(false)
  const [step, setStep] = useState<LoginStep>('credentials')
  const [challengeToken, setChallengeToken] = useState<string | null>(null)

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: defaultLoginValues,
  })

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpFormSchema),
    defaultValues: defaultOtpValues,
  })

  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (data) => {
      if ('requiresTwoFactor' in data && data.requiresTwoFactor) {
        setChallengeToken(data.challengeToken)
        setStep('2fa')
        return
      }
      setAuth(data.user, data.facility)
      if (!data.user.emailVerified) {
        navigate('/verify-email-pending')
      } else {
        navigate('/dashboard')
      }
    },
    onError: (error: AxiosError<ApiError>) => {
      const code = error.response?.data?.error?.code
      const message =
        code === 'RATE_LIMIT_EXCEEDED'
          ? t('errors.rateLimitExceeded')
          : t('errors.invalidCredentials')
      form.setError('root', { type: 'manual', message })
    },
  })

  const verify2FAMutation = useMutation({
    mutationFn: (code: string) => authService.verify2FA({ challengeToken: challengeToken!, code }),
    onSuccess: (data) => {
      setChallengeToken(null)
      setAuth(data.user, data.facility)
      navigate('/dashboard')
    },
    onError: () => {
      otpForm.setError('code', {
        type: 'manual',
        message: t('errors.invalidOtpCode'),
      })
    },
  })

  const onSubmit = (data: LoginFormValues) => {
    loginMutation.mutate(data)
  }

  const onVerify2FA = (data: OtpFormValues) => {
    verify2FAMutation.mutate(data.code)
  }

  const onBackToCredentials = () => {
    setStep('credentials')
    setChallengeToken(null)
    otpForm.reset()
  }

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev)
  }

  return {
    t,
    form,
    otpForm,
    step,
    isLoading: loginMutation.isPending,
    isVerifying: verify2FAMutation.isPending,
    showPassword,
    onSubmit,
    onVerify2FA,
    onBackToCredentials,
    toggleShowPassword,
  }
}
